import { NavLink } from "react-router-dom";
// Direct imports for better tree-shaking (avoids loading entire icon library)
import Squares2X2Icon from "@heroicons/react/24/outline/Squares2X2Icon";
import BoltIcon from "@heroicons/react/24/outline/BoltIcon";
import ChatBubbleLeftRightIcon from "@heroicons/react/24/outline/ChatBubbleLeftRightIcon";
import CalendarDaysIcon from "@heroicons/react/24/outline/CalendarDaysIcon";
import Cog6ToothIcon from "@heroicons/react/24/outline/Cog6ToothIcon";
import UserCircleIcon from "@heroicons/react/24/outline/UserCircleIcon";
import LinkIcon from "@heroicons/react/24/outline/LinkIcon";
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";
import ChevronLeftIcon from "@heroicons/react/24/outline/ChevronLeftIcon";
import ChevronRightIcon from "@heroicons/react/24/outline/ChevronRightIcon";
import { useSidebarStore } from "../../stores/sidebarStore.ts";

const navItems = [
  { to: "/app/dashboard", label: "Dashboard", icon: Squares2X2Icon },
  { to: "/app/training-planner", label: "Training Planner", icon: CalendarDaysIcon },
  { to: "/app/activities", label: "Activities", icon: BoltIcon },
  { to: "/app/chat", label: "Chat", icon: ChatBubbleLeftRightIcon },
];

const settingsItems = [
  { to: "/app/settings/integrations", label: "Integrations", icon: LinkIcon },
  { to: "/app/settings/profile", label: "Profile", icon: UserCircleIcon },
];

export function Sidebar() {
  const { isOpen, isCollapsed, close, toggleCollapsed } = useSidebarStore();

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    if (globalThis.innerWidth < 768) {
      close();
    }
  };

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={close}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed md:static inset-y-0 left-0 z-50
          ${isCollapsed ? "md:w-16" : "w-64"}
          bg-white border-r border-gray-200 flex flex-col
          transform transition-all duration-200 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0
        `}
      >
        {/* Mobile close button */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 md:hidden">
          <span className="text-lg font-semibold text-gray-900">Menu</span>
          <button
            type="button"
            onClick={close}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            aria-label="Close menu"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className={`space-y-1 ${isCollapsed ? "px-2" : "px-3"}`}>
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={handleNavClick}
                  title={isCollapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    `flex items-center ${isCollapsed ? "justify-center" : "gap-3"} px-3 py-2 rounded-lg transition-colors ${
                      isActive
                        ? "bg-brand-100 text-brand-700"
                        : "text-gray-600 hover:bg-brand-50 hover:text-brand-600"
                    }`
                  }
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {!isCollapsed && <span>{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-gray-200 py-4">
          {!isCollapsed && (
            <div className="px-3 mb-2">
              <div className="flex items-center gap-2 px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <Cog6ToothIcon className="w-4 h-4" />
                Settings
              </div>
            </div>
          )}
          <ul className={`space-y-1 ${isCollapsed ? "px-2" : "px-3"}`}>
            {settingsItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={handleNavClick}
                  title={isCollapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    `flex items-center ${isCollapsed ? "justify-center" : "gap-3"} px-3 py-2 rounded-lg transition-colors ${
                      isActive
                        ? "bg-brand-100 text-brand-700"
                        : "text-gray-600 hover:bg-brand-50 hover:text-brand-600"
                    }`
                  }
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {!isCollapsed && <span>{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        {/* Collapse toggle button - desktop only */}
        <div className="hidden md:block border-t border-gray-200 p-2">
          <button
            type="button"
            onClick={toggleCollapsed}
            className={`flex items-center ${isCollapsed ? "justify-center w-full" : "gap-2"} px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors w-full`}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRightIcon className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeftIcon className="w-5 h-5" />
                <span className="text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}
